import {
  Firestore,
  QuerySnapshot,
  query,
  collection,
  orderBy,
  onSnapshot,
  where,
  Timestamp,
  doc,
  DocumentSnapshot,
  runTransaction,
  getDoc,
  deleteField,
  FieldValue,
  Query
} from 'firebase/firestore'
import { FirebaseFirestore } from '@firebase/firestore-types'
import {
  BusinessUnit,
  SeatBooking,
  AndiUserProfile,
  GroupBooking,
  ReportDateRange,
  BookedSeat
} from '../types/andTypes'
import { getInitials, idToNameString, swapElements } from '../utilities/utils'
import dayjs from 'dayjs'
import { ErrorMessages } from '../constants'

const transformSeatBookings = (querySnapshot: QuerySnapshot): SeatBooking[] => {
  const result = querySnapshot.docs.map(doc => {
    const docData = doc.data()
    return {
      ...docData,
      id: doc.id,
      dateBooked: dayjs.unix(docData.dateBooked.seconds).utc().toDate()
    } as SeatBooking
  })
  return result
}

export default class ApiClient {
  // eslint-disable-next-line no-use-before-define
  private static instance: ApiClient
  private db?: FirebaseFirestore | Firestore | undefined

  constructor(db: FirebaseFirestore | Firestore) {
    this.db = db
  }

  public static getInstance(db: FirebaseFirestore | Firestore): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient(db)
    }

    return ApiClient.instance
  }

  subscribeToBookings(cb: (result: SeatBooking[]) => void) {
    const callback = async (querySnapshot: QuerySnapshot) => {
      const bookings: SeatBooking[] = querySnapshot.docs.map(doc => {
        const docData = doc.data()
        const year = +doc.id.slice(0, 4)
        const month = +doc.id.slice(5, 7) - 1
        const day = +doc.id.slice(-2)
        return {
          ...docData,
          id: doc.id,
          dateBooked: new Date(year, month, day)
        } as SeatBooking
      })
      cb(bookings)
    }

    const date = new Date()
    const start = date.setUTCHours(0, 0, 0, 0)
    const end = date.setDate(date.getDate() + 29)

    return onSnapshot(
      query(
        collection(this.db as Firestore, 'booking'),
        where('dateBooked', '>=', new Timestamp(start / 1000, 0)),
        where('dateBooked', '<', new Timestamp(end / 1000, 0)),
        orderBy('dateBooked')
      ),
      callback
    )
  }

  subscribeToReportingBookings(dateRange: ReportDateRange, cb: (result: SeatBooking[]) => void) {
    const callback = async (querySnapshot: QuerySnapshot) => {
      const bookings: SeatBooking[] = transformSeatBookings(querySnapshot)
      cb(bookings)
    }

    const queryObj: Query =
      dateRange.id !== 'start-to-date'
        ? query(
            collection(this.db as Firestore, 'booking'),
            where('dateBooked', '>=', new Timestamp(dateRange.startDate.unix(), 0)),
            where('dateBooked', '<=', new Timestamp(dateRange.endDate.unix(), 0)),
            orderBy('dateBooked')
          )
        : query(
            collection(this.db as Firestore, 'booking'),
            where('dateBooked', '<=', new Timestamp(dateRange.endDate.unix(), 0)),
            orderBy('dateBooked')
          )

    return onSnapshot(queryObj, callback)
  }

  subscribeToANDis(cb: (result: AndiUserProfile[]) => void) {
    const callback = async (querySnapshot: QuerySnapshot) => {
      const ANDis: AndiUserProfile[] = querySnapshot.docs.map(
        doc =>
          ({
            ...doc.data(),
            id: doc.id,
            name: idToNameString(doc.id)
          } as AndiUserProfile)
      )
      cb(ANDis)
    }

    return onSnapshot(query(collection(this.db as Firestore, 'userProfile')), callback)
  }

  async createUserBooking({
    userId,
    name,
    businessUnit,
    dateBooked
  }: {
    userId: string
    name: string
    businessUnit: string
    dateBooked: Date
  }) {
    const db = this.db as Firestore
    const dateString = dayjs(dateBooked).format('YYYY-MM-DD')
    const year = +dateString.slice(0, 4)
    const month = +dateString.slice(5, 7) - 1
    const day = +dateString.slice(-2)
    const timeStamp = new Timestamp(new Date(Date.UTC(year, month, day)).getTime() / 1000, 0)
    return await runTransaction(db, async transaction => {
      const seatBookingRef = doc(db, 'booking', dateString)
      const seatBooking = await transaction.get(seatBookingRef)
      const seat = { businessUnit, userId, name, initials: getInitials(name) }
      const seatBookingData = seatBooking.data() as SeatBooking
      const seats = seatBookingData?.seats ? seatBookingData?.seats : []

      if (seatBookingData?.seats.some(andi => andi.userId === userId)) {
        return Promise.reject(new Error('Sorry! User already booked the same date'))
      }

      const buRef = doc(db, 'businessUnit', businessUnit)
      const buDoc = await getDoc(buRef)
      const buData = buDoc.data() as BusinessUnit

      const isOfficeFullCapacity =
        seats.filter(seat => seat.businessUnit === businessUnit).length === buData.deskCapacity

      if (isOfficeFullCapacity) {
        return Promise.reject(new Error(ErrorMessages.NO_SEATS_AVAILABLE))
      }

      if (seats.filter(seat => seat.businessUnit === businessUnit).length >= buData.deskCapacity) {
        return Promise.reject(new Error('Sorry! Exceeded the maximum allowed'))
      }

      seats.push(seat)

      if (!seatBookingData) {
        transaction.set(seatBookingRef, {
          dateBooked: timeStamp,
          seats,
          lastUpdatedBy: userId
        })
      } else {
        transaction.update(seatBookingRef, { seats, lastUpdatedBy: userId })
      }
    })
  }

  async createGroupBooking({ id, bookingUserId, businessUnit, dateBooked, members }: GroupBooking) {
    const db = this.db as Firestore
    const dateString = dayjs(dateBooked).format('YYYY-MM-DD')
    const year = +dateString.slice(0, 4)
    const month = +dateString.slice(5, 7) - 1
    const day = +dateString.slice(-2)
    const timeStamp = new Timestamp(new Date(Date.UTC(year, month, day)).getTime() / 1000, 0)

    return await runTransaction(db, async transaction => {
      const seatBookingRef = doc(db, 'booking', dateString)
      const seatBooking = await transaction.get(seatBookingRef)
      const seatBookingData = seatBooking.data() as SeatBooking
      const seats = seatBookingData?.seats ? seatBookingData?.seats : []

      // remove user if already booked into BU
      const newMembers = members.filter(
        ({ userId }) =>
          !seatBookingData?.seats.some(
            andi => andi.userId === userId && andi.businessUnit === businessUnit
          )
      )

      // error if user is already booked into a different business unit
      const doubleBookedMsg = newMembers.reduce((acc, { userId, name }) => {
        seatBookingData?.seats.some(andi => {
          if (andi.userId === userId) {
            acc.push(
              `${name} is already booked in ${andi.businessUnit.replace(
                '_',
                ' '
              )} on the selected date.`
            )
            return true
          }
          return false
        })
        return acc
      }, [] as string[])
      if (doubleBookedMsg.length > 0) {
        return Promise.reject(new Error(doubleBookedMsg.join('\n\n')))
      }

      const buRef = doc(db, 'businessUnit', businessUnit)
      const buDoc = await getDoc(buRef)
      const buData = buDoc.data() as BusinessUnit

      const isOfficeFullCapacity =
        seats.filter(seat => seat.businessUnit === businessUnit).length === buData.deskCapacity

      if (isOfficeFullCapacity) {
        return Promise.reject(new Error(ErrorMessages.NO_SEATS_AVAILABLE))
      }

      const totalSeatsRequired =
        seats.filter(seat => seat.businessUnit === businessUnit).length + newMembers.length

      const exceededCapacityBy = totalSeatsRequired - buData.deskCapacity

      if (exceededCapacityBy > 0) {
        return Promise.reject(
          new Error(
            `Please remove ${exceededCapacityBy} name${
              exceededCapacityBy > 1 ? 's' : ''
            } from your booking.`
          )
        )
      }

      // iterate through seats and add them
      newMembers.map(({ name, userId }) =>
        seats.push({
          businessUnit,
          userId,
          name,
          initials: getInitials(name),
          bookingUserId,
          groupId: id
        })
      )

      if (!seatBookingData) {
        transaction.set(seatBookingRef, {
          dateBooked: timeStamp,
          seats,
          lastUpdatedBy: bookingUserId
        })
      } else {
        transaction.update(seatBookingRef, { seats, lastUpdatedBy: bookingUserId })
      }
    })
  }

  async updateGroupBooking(
    addedMembers: BookedSeat[],
    removedMembers: string[],
    dateString: string,
    bookingUserId: string
  ) {
    const db = this.db as Firestore
    return await runTransaction(db, async transaction => {
      const seatBookingRef = doc(db, 'booking', dateString)
      const seatBooking = await transaction.get(seatBookingRef)
      const seatBookingData = seatBooking.data() as SeatBooking
      const seats = seatBookingData?.seats ? seatBookingData?.seats : []

      const updatedSeats = [
        ...seats.filter(seat => !removedMembers.includes(seat.userId)),
        ...addedMembers
      ]

      transaction.update(seatBookingRef, { seats: updatedSeats, lastUpdatedBy: bookingUserId })
    })
  }

  async deleteUserBooking({
    userId,
    dateBooked,
    bookingUserId,
    isOrganiser
  }: {
    userId: string | undefined
    dateBooked: string
    bookingUserId?: string
    isOrganiser?: boolean
  }) {
    const db = this.db as Firestore
    return await runTransaction(db, async transaction => {
      const seatBookingRef = doc(db, 'booking', dateBooked)
      const seatBooking = await transaction.get(seatBookingRef)
      const seatBookingData = seatBooking.data() as SeatBooking
      const existingSeats = seatBookingData?.seats ? seatBookingData?.seats : []

      const seatIndex = existingSeats.findIndex(seat => seat.userId === userId)

      if (seatIndex > -1) {
        existingSeats.splice(seatIndex, 1)
        transaction.update(seatBookingRef, {
          seats: existingSeats,
          lastUpdatedBy: isOrganiser ? bookingUserId : userId
        })
      } else {
        return Promise.reject(Error('The seat you are trying to cancel does not exist'))
      }
    })
  }

  async deleteGroupBooking({
    groupId,
    dateBooked,
    bookingUserId
  }: {
    groupId: string | undefined
    dateBooked: string
    bookingUserId?: string
  }) {
    const db = this.db as Firestore
    return await runTransaction(db, async transaction => {
      const seatBookingRef = doc(db, 'booking', dateBooked)
      const seatBooking = await transaction.get(seatBookingRef)
      const seatBookingData = seatBooking.data() as SeatBooking
      const existingSeats = seatBookingData?.seats ? seatBookingData?.seats : []

      const unrelatedSeats = existingSeats.filter(seat => seat.groupId !== groupId)
      if (!existingSeats.length || existingSeats.length !== unrelatedSeats.length) {
        transaction.update(seatBookingRef, { seats: unrelatedSeats, lastUpdatedBy: bookingUserId })
      } else {
        return Promise.reject(Error('The group booking you are trying to delete does not exist'))
      }
    })
  }

  subscribeToBusinessUnits(cb: (result: BusinessUnit[]) => void) {
    const callback = async (querySnapshot: QuerySnapshot) => {
      const businessUnits: BusinessUnit[] = querySnapshot.docs.map(
        doc =>
          ({
            ...doc.data(),
            id: doc.id
          } as BusinessUnit)
      )
      cb(businessUnits)
    }

    return onSnapshot(
      query(collection(this.db as Firestore, 'businessUnit'), orderBy('name')),
      callback
    )
  }

  subscribeToUserProfile(userId: string, cb: (result: AndiUserProfile) => void) {
    const callback = async (doc: DocumentSnapshot) => {
      const userProfile = {
        ...doc.data(),
        id: doc.id
      } as AndiUserProfile
      cb(userProfile)
    }

    return onSnapshot(doc(this.db as Firestore, 'userProfile', userId), callback)
  }

  async updateUserProfile({
    userId,
    businessUnit,
    team
  }: {
    userId: string
    businessUnit: string
    team?: string
  }) {
    const db = this.db as Firestore
    return await runTransaction(db, async transaction => {
      const docRef = doc(db, 'userProfile', userId)
      const docSnapshot = await transaction.get(docRef)
      const profile: {
        userId: string
        businessUnit: string
        team?: string | FieldValue
      } = { userId, businessUnit }
      if (team) {
        profile.team = team
      } else {
        profile.team = deleteField()
      }

      if (!docSnapshot.data()) {
        transaction.set(docRef, profile)
      } else {
        transaction.update(docRef, profile)
      }
    })
  }

  async reAssignGroupOrganiser({
    groupId,
    dateBooked,
    newOrganiserUserId,
    includeFlag,
    oldOrganiserId
  }: {
    groupId: string | undefined
    dateBooked: string
    newOrganiserUserId: string
    includeFlag?: boolean
    oldOrganiserId?: string
  }) {
    const db = this.db as Firestore

    return await runTransaction(db, async transaction => {
      const groupBookingRef = doc(db, 'booking', dateBooked)
      const groupBooking = await transaction.get(groupBookingRef)
      const groupBookingData = groupBooking.data() as SeatBooking
      const existingSeats = groupBookingData?.seats ? groupBookingData.seats : []

      if (existingSeats.length === 0) return Promise.reject(Error('Booking not found'))

      if (!existingSeats.some(i => i.userId === newOrganiserUserId && i.groupId === groupId)) {
        return Promise.reject(Error('This user does not exist on this booking'))
      }

      let newOrganiserIndex = 0
      let oldOrganiserIndex = 0

      const newSeats = existingSeats.map((seat, index) => {
        if (seat.groupId === groupId) {
          if (seat.userId === newOrganiserUserId) newOrganiserIndex = index
          else if (seat.userId === seat.bookingUserId) {
            oldOrganiserIndex = index
            return {
              ...seat,
              bookingUserId: newOrganiserUserId,
              isOldOrganiser: !!includeFlag
            }
          }
          return { ...seat, bookingUserId: newOrganiserUserId, lastUpdatedBy: oldOrganiserId }
        } else {
          return seat
        }
      })
      transaction.update(groupBookingRef, {
        seats: swapElements(newSeats, oldOrganiserIndex, newOrganiserIndex)
      })
    })
  }
}
